import { useRef, useState, useCallback, useEffect } from 'react';
import { useInView } from '@/hooks/useInView';
import { newBooks2026 } from '@/mocks/books';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../../shared/book-publication-status';

const getThemeBadgeClass = (theme: string): string => {
  const map: Record<string, string> = {
    '商業理財': 'bg-accent-100 text-accent-800',
    '政治與社會': 'bg-secondary-100 text-secondary-800',
    '以古諷今': 'bg-primary-100 text-primary-800',
    '人文社科': 'bg-accent-100 text-accent-800',
  };
  return map[theme] || 'bg-accent-100 text-accent-800';
};

export default function NewBooks2026Section() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const { ref: refHeader, inView: inViewHeader } = useInView();
  const { ref: refCatalog, inView: inViewCatalog } = useInView();

  const anim = (inViewFlag: boolean) =>
    `transition-all duration-700 ease-out ${inViewFlag ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll]);

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    if (!card) return;
    const gap = parseInt(getComputedStyle(el).gap || '16', 10);
    const step = card.offsetWidth + gap;
    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
  }, []);

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto w-full">

        {/* ───── Section Header ───── */}
        <div ref={refHeader} className={`max-w-3xl mx-auto text-center ${anim(inViewHeader)}`}>
          <p className="text-base font-label tracking-widest text-foreground-500 mb-3 uppercase">New Releases</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-3">
            2026 最新發行
          </h2>
          <p className="text-base md:text-lg text-foreground-500 leading-relaxed max-w-2xl mx-auto">
            探索山頂視角於 2026 年最新發行的商業理財、政治與人文社科書籍。
          </p>
        </div>

        {/* ───── Carousel ───── */}
        <div className={`mt-14 relative ${anim(inViewHeader)}`}>
          {/* Scroll container */}
          <div
            ref={scrollRef}
            className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 scrollbar-hide"
          >
            {newBooks2026.map((book) => (
              <article
                key={book.id}
                className="group flex flex-col flex-shrink-0 snap-start bg-background-100 rounded-lg overflow-hidden border border-background-200/60 hover:bg-background-50 transition-colors duration-300 w-[85%] sm:w-[48%] lg:w-[calc(33.333%-16px)]"
              >
                {/* Cover Image */}
                <div className="aspect-[3/4] overflow-hidden bg-background-200/30">
                  <img
                    src={book.coverUrl}
                    alt={`《${book.title}》書籍封面`}
                    title={`《${book.title}》— ${book.theme}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {(() => {
                    const publishDate = 'publishDate' in book && typeof book.publishDate === 'string' ? book.publishDate : '';
                    const badge = getBookPublicationBadge(publishDate);
                    return badge ? (
                      <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(badge)}`}>
                        {getBookPublicationBadgeLabel(badge)}
                      </span>
                    ) : null;
                  })()}
                </div>

                {/* Card Content */}
                <div className="flex flex-col flex-1 p-5 md:p-6">
                  <span
                    className={`inline-block self-start px-3 py-1 text-xs font-label rounded-full mb-3 ${getThemeBadgeClass(book.theme)}`}
                  >
                    {book.theme}
                  </span>

                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-1 leading-snug">
                    {book.title}
                  </h3>

                  {book.subtitle && (
                    <p className="text-sm text-foreground-600 mb-1 leading-snug">
                      {book.subtitle}
                    </p>
                  )}

                  <p className="text-sm text-foreground-500 mb-3">{book.author}</p>

                  <p className="text-sm text-foreground-500 leading-relaxed mb-5 flex-1">
                    {book.description}
                  </p>

                  <a
                    href={book.catalogUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    查看書籍資訊
                    <i className="ri-arrow-right-line text-sm"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>

          {/* Left Arrow */}
          <button
            type="button"
            onClick={() => scrollBy('left')}
            aria-label="向左瀏覽"
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-5 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/90 border border-background-200/60 flex items-center justify-center text-foreground-700 hover:text-foreground-950 hover:bg-background-50 transition-all duration-200 z-10 shadow-sm ${canScrollLeft ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
          </button>

          {/* Right Arrow */}
          <button
            type="button"
            onClick={() => scrollBy('right')}
            aria-label="向右瀏覽"
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-5 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/90 border border-background-200/60 flex items-center justify-center text-foreground-700 hover:text-foreground-950 hover:bg-background-50 transition-all duration-200 z-10 shadow-sm ${canScrollRight ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
          </button>
        </div>

        {/* ───── 書籍內容原則說明 ───── */}
        <p className="mt-12 max-w-3xl mx-auto text-center text-sm text-foreground-400 leading-relaxed">
          書籍內容僅屬於出版與一般知識閱讀，不代表提供專業投資、金融、政治或法律服務。
          政治與以古諷今類書籍呈現個別作品的內容與觀點，不代表山頂視角或崧博出版支持特定政黨、候選人或政治立場。
        </p>

        {/* ───── Bottom Catalog CTA ───── */}
        <div ref={refCatalog} className={`mt-10 ${anim(inViewCatalog)}`}>
          <div className="max-w-xl mx-auto text-center bg-background-100 rounded-lg p-8 md:p-10 border border-background-200/60">
            <div className="w-12 h-12 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-book-open-line text-lg text-accent-700"></i>
            </div>
            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-2">
              查詢完整書目資訊
            </h3>
            <p className="text-base text-foreground-500 leading-relaxed mb-6">
              完整書名、作者、內容主題及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
            </p>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-label font-medium text-base rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              前往書籍資料庫
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
